import { strapiClient } from "@shared/api";
import rebuild from "@shared/api/rebuild";

import { mapDtoToAccommodationsPoints } from "@/_pages/accommodations/model";
import { mapDtoToCafesRestaurantsPoints } from "@/_pages/cafes-restaurants/model";
import { mapDtoToCulturalObjectsPoints } from "@/_pages/cultural-objects";
import { mapDtoToWebCams } from "@/_pages/web-cams/model";

import {
  type MapPageInfoType,
  type MapPageType,
  mapDtoToTrails,
} from "../model";
import {
  mapDtoToMapEcotraps,
  mapDtoToMapPageInfo,
  mapDtoToPage,
  mapDtoToStations,
} from "../model/mappers";
import { searchParams } from "./params";

/**
 * Сингл-тайп "Карта": заголовок страницы (h1).
 * Если запись ещё не создана/не опубликована в Strapi — не роняем страницу.
 */
export const getMapPageInfo = async (): Promise<MapPageInfoType | null> => {
  try {
    const mapPage = await strapiClient.single("map-page").find();

    return rebuild(mapDtoToMapPageInfo(mapPage)) as MapPageInfoType;
  } catch {
    return null;
  }
};

export const getMapPage = async () => {
  const trailsData = strapiClient.collection("trails");
  const ecotrapsData = strapiClient.collection("ecotraps");
  const stationsData = strapiClient.collection("stations");
  const accommodationsData = strapiClient.collection("accommodations-points");
  const restaurantsData = strapiClient.collection("cafes-restaurants-points");
  const webcamsData = strapiClient.collection("web-cams");
  const culturalObjectsData = strapiClient.collection("cultural-objects");
  const pageData = strapiClient.single("trails-and-cable-ways-page");

  const trails = await trailsData.find({
    ...searchParams.trail,
  });

  const ecotraps = await ecotrapsData.find({
    ...searchParams.ecotraps,
  });

  const stations = await stationsData.find({
    ...searchParams.stations,
  });

  const page = await pageData.find({
    ...searchParams.page,
  });

  const webcams = await webcamsData.find({
    ...searchParams.webCams,
  });

  const restaurants = await restaurantsData.find({
    ...searchParams.restaurants,
  });

  const accommodations = await accommodationsData.find({
    ...searchParams.accommodations,
  });

  const culturalObjects = await culturalObjectsData.find({
    ...searchParams.culturalObjects,
  });

  return rebuild({
    trails: mapDtoToTrails(trails),
    ecotraps: mapDtoToMapEcotraps(ecotraps),
    stations: mapDtoToStations(stations),
    page: mapDtoToPage(page),
    webcams: mapDtoToWebCams(webcams).data,
    "cafes-restaurants": mapDtoToCafesRestaurantsPoints(restaurants).data,
    accommodations: mapDtoToAccommodationsPoints(accommodations).data,
    "cultural-objects": mapDtoToCulturalObjectsPoints(culturalObjects).data,
  }) as MapPageType;
};

export const getMapPoints = async () => {
  const accommodationsData = strapiClient.collection("accommodations-points");
  const restaurantsData = strapiClient.collection("cafes-restaurants-points");
  const webcamsData = strapiClient.collection("web-cams");
  const culturalObjectsData = strapiClient.collection("cultural-objects");

  const [webcams, restaurants, accommodations, culturalObjects] =
    await Promise.all([
      webcamsData.find({
        ...searchParams.webCams,
      }),
      restaurantsData.find({
        ...searchParams.restaurants,
      }),
      accommodationsData.find({
        ...searchParams.accommodations,
      }),
      culturalObjectsData.find({
        ...searchParams.culturalObjects,
      }),
    ]);

  return rebuild({
    webcams: mapDtoToWebCams(webcams).data,
    "cafes-restaurants": mapDtoToCafesRestaurantsPoints(restaurants).data,
    accommodations: mapDtoToAccommodationsPoints(accommodations).data,
    "cultural-objects": mapDtoToCulturalObjectsPoints(culturalObjects).data,
  }) as Pick<
    MapPageType,
    "webcams" | "cafes-restaurants" | "accommodations" | "cultural-objects"
  >;
};
